/**
 * MainIdeaWebSVG — Phase 1, Component 5 of the TeachBud Representation Catalog
 *
 * Renders a main idea web (spider map) per §5.2 Reading Comprehension — Informational:
 *   - Central oval: "Main Idea" (student fills in)
 *   - Three satellite ovals connected by lines: "Detail 1/2/3"
 *   - Grades 2–3: Large ovals with 3 writing lines each
 *   - Grades 4–5: Standard size with 4–5 writing lines each
 *   - Grade 6+: May include a "How it supports…" extension box
 *   - Student mode: blank ovals with ruled lines inside
 *   - Teacher mode: content provided
 */

import React from 'react';

export interface MainIdeaWebProps {
  mainIdea?: string | null;
  details?: (string | null)[];
  detailCount?: number;
  studentMode?: boolean;
  width?: number;
  grade?: number;
  className?: string;
}

// ── Layout ────────────────────────────────────────────────────────────────────
const VB_W = 540;
const VB_H = 400;
const CX = VB_W / 2;
const CY = VB_H / 2;

// Center oval
const CENTER_RX = 90;
const CENTER_RY = 52;

// Detail ovals
const DETAIL_RX = 100;
const DETAIL_RY = 44;

// Detail positions (3 satellites: top-left, top-right, bottom-center)
const DETAIL_POSITIONS_3 = [
  { cx: 120, cy: 110 },
  { cx: 420, cy: 110 },
  { cx: 270, cy: 340 },
];
const DETAIL_POSITIONS_4 = [
  { cx: 100, cy: 100 },
  { cx: 440, cy: 100 },
  { cx: 100, cy: 320 },
  { cx: 440, cy: 320 },
];

// Colors (ELA = violet)
const CENTER_FILL  = '#7C3AED';  // violet-600
const CENTER_LIGHT = '#F5F3FF';  // violet-50
const DETAIL_FILL  = '#DDD6FE';  // violet-200
const DETAIL_LIGHT = '#FAF5FF';  // violet-50 lighter
const LINE_COLOR   = '#8B5CF6';  // violet-500
const LABEL_COLOR  = '#5B21B6';  // violet-800
const TEXT_COLOR   = '#1E3A5F';
const RULE_COLOR   = '#E5E7EB';

export function MainIdeaWebSVG({
  mainIdea,
  details,
  detailCount = 3,
  studentMode = true,
  width,
  grade = 3,
  className = '',
}: MainIdeaWebProps) {
  const nDetails = Math.max(2, Math.min(4, detailCount));
  const positions = nDetails === 4 ? DETAIL_POSITIONS_4 : DETAIL_POSITIONS_3;
  const linesN = grade <= 3 ? 2 : 3;
  const LABEL_FS = grade <= 2 ? 13 : 11;
  const DETAIL_FS = grade <= 2 ? 11 : 9;

  const svgW = width ?? VB_W;
  const svgH = width ? Math.round((VB_H / VB_W) * width) : VB_H;

  // Line from center oval edge toward each detail center
  function connectorLine(detCX: number, detCY: number) {
    const dx = detCX - CX;
    const dy = detCY - CY;
    const len = Math.sqrt(dx * dx + dy * dy);
    const startX = CX + (dx / len) * CENTER_RX;
    const startY = CY + (dy / len) * CENTER_RY;
    const endX   = detCX - (dx / len) * DETAIL_RX;
    const endY   = detCY - (dy / len) * DETAIL_RY;
    return { x1: startX, y1: startY, x2: endX, y2: endY };
  }

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width={svgW} height={svgH}
      className={className}
      role="img"
      aria-label={`Main idea web with ${nDetails} details`}
    >
      <title>Main idea web</title>

      {/* Connector lines */}
      {positions.map((pos, i) => {
        const { x1, y1, x2, y2 } = connectorLine(pos.cx, pos.cy);
        return (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={LINE_COLOR} strokeWidth={2} strokeDasharray="5 3" />
        );
      })}

      {/* Center oval shadow */}
      <ellipse cx={CX + 3} cy={CY + 3} rx={CENTER_RX} ry={CENTER_RY} fill="#C4B5FD" opacity={0.4} />

      {/* Center oval */}
      <ellipse cx={CX} cy={CY} rx={CENTER_RX} ry={CENTER_RY} fill={CENTER_LIGHT} stroke={CENTER_FILL} strokeWidth={2.5} />
      <text x={CX} y={CY - 6} textAnchor="middle" fontSize={LABEL_FS} fontFamily="Nunito, sans-serif" fontWeight={700} fill={LABEL_COLOR}>
        Main Idea
      </text>
      {studentMode ? (
        <>
          <line x1={CX - CENTER_RX + 12} y1={CY + 10} x2={CX + CENTER_RX - 12} y2={CY + 10} stroke={RULE_COLOR} strokeWidth={1} />
          <line x1={CX - CENTER_RX + 12} y1={CY + 22} x2={CX + CENTER_RX - 12} y2={CY + 22} stroke={RULE_COLOR} strokeWidth={1} />
        </>
      ) : mainIdea ? (
        <text x={CX} y={CY + 12} textAnchor="middle" fontSize={DETAIL_FS} fontFamily="Inter, sans-serif" fill={TEXT_COLOR}>
          {mainIdea.slice(0, 28)}
        </text>
      ) : null}

      {/* Detail ovals */}
      {positions.map((pos, i) => {
        const content = !studentMode ? (details?.[i] ?? null) : null;
        const lineSpacing = (DETAIL_RY * 2 - 24) / Math.max(1, linesN);

        return (
          <g key={i}>
            {/* Shadow */}
            <ellipse cx={pos.cx + 2} cy={pos.cy + 2} rx={DETAIL_RX} ry={DETAIL_RY} fill={DETAIL_FILL} opacity={0.5} />
            {/* Oval */}
            <ellipse cx={pos.cx} cy={pos.cy} rx={DETAIL_RX} ry={DETAIL_RY} fill={DETAIL_LIGHT} stroke={DETAIL_FILL} strokeWidth={2} />
            {/* "Detail N" label */}
            <text x={pos.cx} y={pos.cy - DETAIL_RY + 16} textAnchor="middle" fontSize={DETAIL_FS} fontFamily="Nunito, sans-serif" fontWeight={700} fill={LABEL_COLOR}>
              {`Detail ${i + 1}`}
            </text>
            {/* Content or ruled lines */}
            {content ? (
              <text x={pos.cx} y={pos.cy + 6} textAnchor="middle" fontSize={DETAIL_FS} fontFamily="Inter, sans-serif" fill={TEXT_COLOR}>
                {content.slice(0, 28)}
              </text>
            ) : (
              Array.from({ length: linesN }).map((_, li) => {
                const ly = pos.cy - DETAIL_RY + 24 + (li + 1) * lineSpacing;
                return (
                  <line key={li} x1={pos.cx - DETAIL_RX + 10} y1={ly} x2={pos.cx + DETAIL_RX - 10} y2={ly} stroke={RULE_COLOR} strokeWidth={1} />
                );
              })
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function parseMainIdeaWebHint(hint: string): MainIdeaWebProps {
  const countMatch = hint.match(/(\d+)\s*detail/i);
  return { detailCount: countMatch ? Math.min(4, parseInt(countMatch[1])) : 3, studentMode: true };
}
